/**
 * @param {Object} error - Axios error object or custom error
 * @returns {Object} - { message: string, errorType: string, shouldDisplay: boolean }
 */
export const mapErrorToUI = (error) => {
  // Log the full error for debugging
  console.log('mapErrorToUI received:', error);
  
  // Handle 401 - log only, don't show UI message
  if (error.response?.status === 401) {
    console.error('Authentication error:', error);
    return {
      message: null,
      errorType: 'AUTH_ERROR',
      shouldDisplay: false,
    };
  }

  // Handle network errors (offline, no connection)
  if (!error.response && error.message === 'Network Error') {
    return {
      message: 'No internet connection. Please check your network and try again.',
      errorType: 'NETWORK_ERROR',
      shouldDisplay: true,
    };
  }

  // Handle timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return {
      message: 'Request timeout. Please try again.',
      errorType: 'TIMEOUT_ERROR',
      shouldDisplay: true,
    };
  }


  if (error.response?.data) {
    const { message, code, status, reason, error: errorCode } = error.response.data;
    
 
    console.log('Backend error response:', { message, code, status, reason, errorCode });
    
   
    const errorMessage = message || reason || 'An error occurred. Please try again.';
    
 
    let errorType = 'UNKNOWN_ERROR';

    const httpStatus = error.response?.status;

    const msgLower = (message || '').toLowerCase();
    const reasonLower = (reason || '').toLowerCase();
    const errorCodeLower = (errorCode || '').toLowerCase();

    const backendCodeToErrorType = {
      service_unavailable: 'SERVICE_UNAVAILABLE',
      no_usable_rooms: 'NO_USABLE_ROOMS',
      dimensions_missing: 'NO_DIMENSIONS',
      mixed_scale: 'MIXED_SCALE',
      furniture_detected: 'FURNITURE_DETECTED',
      multiple_pages: 'MULTI_PAGE_PDF',
      file_unreadable: 'FILE_UNREADABLE',
      // MIME / magic-byte rejection (uploadPipeline)
      invalid_file: 'INVALID_FILE_TYPE',
      // AI content rejection — valid image that is not a floor plan
      not_a_floor_plan: 'NOT_A_FLOOR_PLAN',
      not_floor_plan: 'NOT_A_FLOOR_PLAN',
      file_content: 'INVALID_FILE_TYPE',
      file_quality: 'FILE_UNREADABLE',
      size_unsupported: 'PLAN_SIZE_UNSUPPORTED',
      scale_verification_failed: 'MIXED_SCALE',
      room_scale_error: 'NO_DIMENSIONS',
      network_error: 'SERVICE_UNAVAILABLE',
      network_timeout: 'SERVICE_UNAVAILABLE',
      internal_error: 'SERVICE_UNAVAILABLE',
      internal_error_ai: 'SERVICE_UNAVAILABLE',
      internal_error_docker: 'SERVICE_UNAVAILABLE',
      internal_error_db: 'SERVICE_UNAVAILABLE',
      internal_error_layout: 'SERVICE_UNAVAILABLE',
      internal_error_auto_furnish: 'SERVICE_UNAVAILABLE',
      internal_error_no_rooms: 'NO_USABLE_ROOMS',
    };

    if (backendCodeToErrorType[errorCodeLower]) {
      errorType = backendCodeToErrorType[errorCodeLower];
    }

    const looksLikeNotAFloorPlan =
      msgLower.includes('not a floor plan') ||
      msgLower.includes('not detected as a floor plan') ||
      msgLower.includes('does not appear to be a floor plan') ||
      msgLower.includes('please upload a valid floor plan');

    // Content rejection must win over MIME invalid_file when the message
    // clearly says the image isn't a floor plan (legacy backends still emit
    // invalid_file for this case).
    if (looksLikeNotAFloorPlan) {
      errorType = 'NOT_A_FLOOR_PLAN';
    }

    // Content / plan validation (true 400s). Do NOT treat every body with
    // status:"error" as validation — that wrongly swallows 413/429/5xx where
    // HTTP status should win (file too large, rate limit, outage).
    if (
      httpStatus === 400 ||
      code === 400 ||
      (status === 'error' &&
        (httpStatus == null || httpStatus === 400) &&
        (code == null || code === 400))
    ) {
      
      const isInvalidTypeMessage =
        msgLower.includes('unsupported format') ||
        msgLower.includes('invalid file type') ||
        msgLower.includes('file type mismatch') ||
        reasonLower.includes('jpg') ||
        reasonLower.includes('png') ||
        reasonLower.includes('pdf');

      if (errorType === 'NOT_A_FLOOR_PLAN') {
        // already classified
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (isInvalidTypeMessage ||
          errorCodeLower === 'invalid_file' ||
          ((errorCodeLower === 'upload_error' ||
            errorCodeLower === 'validation_error') &&
            isInvalidTypeMessage))
      ) {
        errorType = 'INVALID_FILE_TYPE';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (msgLower.includes('plan size') ||
          msgLower.includes('10 mb') ||
          msgLower.includes('file size') ||
          msgLower.includes('upload under') ||
          msgLower.includes('too large') ||
          msgLower.includes('payload too large') ||
          errorCodeLower === 'size_unsupported')
      ) {
        errorType = 'PLAN_SIZE_UNSUPPORTED';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (msgLower.includes('no image file') || msgLower.includes('no file'))
      ) {
        errorType = 'NO_FILE';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        ((msgLower.includes('pdf') && msgLower.includes('more than 1 page')) ||
          msgLower.includes('multiple pages') ||
          errorCodeLower === 'multiple_pages')
      ) {
        errorType = 'MULTI_PAGE_PDF';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (msgLower.includes('invalid pdf') ||
          msgLower.includes('unreadable') ||
          msgLower.includes('corrupted') ||
          msgLower.includes('clearer version') ||
          errorCodeLower === 'file_unreadable')
      ) {
        errorType = 'FILE_UNREADABLE';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (msgLower.includes('dimensions') ||
          msgLower.includes('add visible dimensions') ||
          msgLower.includes('dimensions not visible') ||
          errorCodeLower === 'dimensions_missing')
      ) {
        errorType = 'NO_DIMENSIONS';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (msgLower.includes('furniture') ||
          msgLower.includes('furniture detected') ||
          errorCodeLower === 'furniture_detected')
      ) {
        errorType = 'FURNITURE_DETECTED';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (msgLower.includes('mixed scale') ||
          msgLower.includes('uniform scale') ||
          msgLower.includes('scale validation failed') ||
          reasonLower.includes('mixed_scale') ||
          errorCodeLower === 'mixed_scale')
      ) {
        errorType = 'MIXED_SCALE';
      } else if (
        errorType === 'UNKNOWN_ERROR' &&
        (msgLower.includes('size_unsupported') ||
          msgLower.includes('dimensions/scale not supported') ||
          errorCodeLower === 'size_unsupported')
      ) {
        errorType = 'SIZE_UNSUPPORTED';
      } else if (errorType === 'UNKNOWN_ERROR') {
        errorType = 'VALIDATION_ERROR';
      }
    } else if (httpStatus === 403 || code === 403) {
      errorType = 'FORBIDDEN';
    } else if (httpStatus === 408 || code === 408) {
      errorType =
        errorType === 'UNKNOWN_ERROR' ? 'SERVICE_UNAVAILABLE' : errorType;
    } else if (httpStatus === 413 || code === 413) {
      errorType = 'PAYLOAD_TOO_LARGE';
    } else if (httpStatus === 415 || code === 415) {
      errorType = 'UNSUPPORTED_MEDIA_TYPE';
    } else if (httpStatus === 422 || code === 422) {
      errorType = 'FILE_UNREADABLE';
    } else if (httpStatus === 429 || code === 429) {
      errorType = 'RATE_LIMIT';
    } else if (
      httpStatus === 503 ||
      code === 503 ||
      (msgLower && msgLower.includes('unavailable'))
    ) {
      errorType =
        errorType === 'UNKNOWN_ERROR' ? 'SERVICE_UNAVAILABLE' : errorType;
    } else if (httpStatus >= 500 || code >= 500) {
      errorType =
        errorType === 'UNKNOWN_ERROR' ? 'SERVICE_UNAVAILABLE' : errorType;
    }
    
    console.log('Mapped error type:', errorType);
    
    return {
      message: errorMessage,
      errorType,
      shouldDisplay: true,
    };
  }

  // Handle polling timeout (frontend-side)
  if (error.isPollingTimeout) {
    return {
      message: 'Processing is taking longer than expected. Please try again later.',
      errorType: 'POLLING_TIMEOUT',
      shouldDisplay: true,
    };
  }

  // Handle job failed status
  if (error.isJobFailed) {
    const jobMsg = (error.message || '').toLowerCase();
    const jobLooksLikeNotAFloorPlan =
      jobMsg.includes('not a floor plan') ||
      jobMsg.includes('not detected as a floor plan') ||
      jobMsg.includes('does not appear to be a floor plan') ||
      jobMsg.includes('please upload a valid floor plan') ||
      jobMsg === 'not_a_floor_plan' ||
      jobMsg === 'not_floor_plan';

    return {
      message: error.message || 'Processing failed. Please try again.',
      errorType: jobLooksLikeNotAFloorPlan ? 'NOT_A_FLOOR_PLAN' : 'JOB_FAILED',
      shouldDisplay: true,
    };
  }

  // Default error
  return {
    message: 'An unexpected error occurred. Please try again.',
    errorType: 'UNKNOWN_ERROR',
    shouldDisplay: true,
  };
};

/**

 * @param {string} errorType - Error type from mapErrorToUI
 * @returns {string} - Error title
 */
export const getErrorTitle = (errorType) => {
  const titles = {
    INVALID_FILE_TYPE: 'Unsupported File Format',
    FILE_TOO_LARGE: 'File Too Large',
    PAYLOAD_TOO_LARGE: 'File Too Large',
    NO_FILE: 'No File Selected',
    MULTI_PAGE_PDF: 'Unsupported File Format',
    INVALID_PDF: 'File Unreadable',
    NO_DIMENSIONS: 'Dimensions Not Found',
    SIZE_UNSUPPORTED: 'Dimensions Not Supported',
    MIXED_SCALE: 'Mixed Scale Detected',
    NO_USABLE_ROOMS: 'No Usable Rooms',
    NOT_A_FLOOR_PLAN: 'Could Not Analyze Plan',
    VALIDATION_ERROR: 'Validation Error',
    FORBIDDEN: 'Access Forbidden',
    UNSUPPORTED_MEDIA_TYPE: 'Unsupported File Type',
    SERVICE_UNAVAILABLE: 'Service Unavailable',
    TIMEOUT_ERROR: 'Request Timeout',
    RATE_LIMIT: 'Too Many Requests',
    SERVER_ERROR: 'Server Error',
    NETWORK_ERROR: 'Network Error',
    POLLING_TIMEOUT: 'Processing Timeout',
    JOB_FAILED: 'Processing Failed',
    UNKNOWN_ERROR: 'Error',
  };
  
  return titles[errorType] || 'Error';
};

/**
 * @param {string} errorType - Error type from mapErrorToUI
 * @returns {boolean} - Whether the error is retryable
 */
export const isRetryableError = (errorType) => {
  const retryableErrors = [
    'NETWORK_ERROR',
    'TIMEOUT_ERROR',
    'SERVICE_UNAVAILABLE',
    'SERVER_ERROR',
    'POLLING_TIMEOUT',
    'JOB_FAILED',
    'UNKNOWN_ERROR',
  ];
  
  return retryableErrors.includes(errorType);
};

export default mapErrorToUI;

